import { useState, useEffect } from 'react';
import axios, { AxiosError } from 'axios';
import { getHeaderValue, isApiRequestUrl, parseMaintenanceRefusal, recordMaintenanceRefusal } from './maintenance';

/**
 * Registers an Axios response interceptor that discriminates between the two
 * things a 503 can mean, and returns true only for the second.
 *
 * - A **typed maintenance 503** (a JSON body carrying `code: "MAINTENANCE"`) is
 *   a deliberate read-only window. It is recorded as a refusal so the UI can
 *   explain it, and this hook keeps returning false — the app stays browsable
 *   behind the maintenance banner instead of blacking out. Without this, under
 *   the read-only gate a single refused write would take the whole app down.
 * - **Any other 503** from a platform API is an unexplained failure, i.e. a
 *   genuine outage, and still returns true so the caller renders the
 *   full-screen maintenance/outage page exactly as before. That preserves the
 *   de facto error fallback this interceptor has always provided, which is why
 *   the takeover is discriminated rather than deleted (NS-2461).
 *
 * The one exception is a typed window that reports `readsAvailable: false`:
 * nothing is being served, so the takeover is the honest presentation.
 *
 * @returns Whether the caller should render the full-screen outage page.
 */
export function useMaintenanceMode(): boolean {
  const [isMaintenanceMode, setIsMaintenanceMode] = useState(false);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (response) => response,
      (err: AxiosError) => {
        if (err?.response?.status === 503) {
          const refusal = parseMaintenanceRefusal({
            status: err.response.status,
            body: err.response.data,
            retryAfter: getHeaderValue(err.response.headers, 'retry-after'),
          });

          if (refusal) {
            recordMaintenanceRefusal(refusal);
            // A read-only window must not black the app out; a window with no
            // reads available has nothing to show, so it still takes over.
            if (!refusal.readsAvailable) setIsMaintenanceMode(true);
          } else if (isApiRequestUrl(err.config?.url)) {
            setIsMaintenanceMode(true);
          }
        }

        return Promise.reject(err);
      },
    );

    return () => axios.interceptors.response.eject(id);
  }, []);

  return isMaintenanceMode;
}
